import { Button, Flex, Form } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import type { ValidateErrorEntity } from "rc-field-form/es/interface"
import React, { useRef, useState } from "react"

import { FormChangedWatcher } from "../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import { DocumentPaymentScheduleSection } from "../../../../components/Proposal&Contracts/DocumentPaymentScheduleSection.tsx"
import { DocumentScopeSection } from "../../../../components/Proposal&Contracts/DocumentScopeSection/DocumentScopeSection.tsx"
import type { DocumentScopeSectionListRef } from "../../../../components/Proposal&Contracts/DocumentScopeSection/DocumentScopeSectionList.tsx"
import type { DocumentScopeSectionProjectTypes } from "../../../../components/Proposal&Contracts/document.types.ts"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { hasFieldErrors } from "../../../../utils/form/has-field-errors.ts"
import { type ContractFormData } from "./contract-form-data.tsx"
import { ContractClientInformationSection } from "./sections/ContractClientInformationSection.tsx"
import { ContractClientResponsibilitiesSection } from "./sections/ContractClientResponsibilitiesSection.tsx"
import { ContractCompanySection } from "./sections/ContractCompanySection.tsx"
import { ContractCostSummarySection } from "./sections/ContractCostSummarySection.tsx"
import { ContractExclusionsSection } from "./sections/ContractExclusionsSection.tsx"
import { ContractInclusionsSection } from "./sections/ContractInclusionsSection.tsx"
import { ContractProjectOverviewSection } from "./sections/ContractProjectOverviewSection.tsx"
import { ContractTermsSection } from "./sections/ContractTermsSection.tsx"
import { ContractWarrantySection } from "./sections/ContractWarrantySection.tsx"

interface ContractFormProps {
  innerForm?: FormInstance<ContractFormData>
  onSubmit: (values: Partial<ContractFormData>) => void
  initialValues?: Partial<ContractFormData>
  projectTypes: DocumentScopeSectionProjectTypes
  saving?: boolean
  checkDirty?: boolean
}

export const ContractForm = ({
  innerForm,
  onSubmit,
  saving,
  projectTypes = [],
  initialValues,
  checkDirty = true
}: ContractFormProps) => {
  const { normalizeDate } = useDateFormat()

  const [form] = Form.useForm<ContractFormData>(innerForm)
  const detailedScopeListRef = useRef<DocumentScopeSectionListRef>(null)
  const [hasErrors, setHasErrors] = useState(false)

  const onSubmitHandler = ({ project, ...data }: ContractFormData) => {
    onSubmit({
      ...data,
      project: {
        ...project,
        start_date: normalizeDate(project?.start_date),
        completion_date: normalizeDate(project?.completion_date)
      }
    })
  }

  const onFinishFailed = async (e: ValidateErrorEntity<ContractFormData>) => {
    const firstError = e?.errorFields?.[0]
    const isPhaseError = firstError?.name?.includes(
      "project_phases" as keyof ContractFormData
    )

    if (!isPhaseError) {
      return form.scrollToField(firstError?.name, {
        scrollMode: "always",
        behavior: "smooth",
        block: "center"
      })
    }

    const phaseIndex = firstError?.name?.[1] as number
    const phases = e?.values?.project_phases || []
    const projectType = phases?.[phaseIndex]?.project_type

    if (!projectType) return

    detailedScopeListRef?.current?.setCollapsedActiveKey([projectType])
    await new Promise((res) => setTimeout(res, 300))
    form.scrollToField(firstError?.name, {
      scrollMode: "always",
      behavior: "smooth",
      block: "center"
    })
  }

  return (
    <Form<ContractFormData>
      form={form}
      clearOnDestroy
      onFinish={onSubmitHandler}
      onFinishFailed={onFinishFailed}
      onFieldsChange={(_, allFields) => setHasErrors(hasFieldErrors(allFields))}
      name="dynamic_form_item"
      layout="vertical"
      className={"flex flex-col gap-3 pr-2 with-default-error"}
      validateTrigger={["onSubmit", "onChange"]}
      disabled={saving}
      initialValues={{
        ...initialValues,
        cost_summary: {
          ...initialValues?.cost_summary,
          line_items: initialValues?.cost_summary?.line_items || []
        },
        project_phases: initialValues?.project_phases || [],
        inclusions: initialValues?.inclusions || [],
        exclusions: initialValues?.exclusions || [],
        client_responsibilities: initialValues?.client_responsibilities || [],
        terms_and_conditions: initialValues?.terms_and_conditions || [],
        payment_schedule: initialValues?.payment_schedule || [],
        warranty: {
          description: initialValues?.warranty?.description || "",
          items: initialValues?.warranty?.items || []
        }
      }}
    >
      <FormChangedWatcher<ContractFormData>
        form={form}
        hidden={!checkDirty}
        debug
      />

      <ContractCompanySection form={form} />
      <ContractClientInformationSection />
      <ContractProjectOverviewSection form={form} />
      <DocumentScopeSection
        listRef={detailedScopeListRef}
        form={form}
        projectTypes={projectTypes}
      />
      <ContractInclusionsSection />
      <ContractExclusionsSection />
      <ContractClientResponsibilitiesSection />
      <ContractCostSummarySection form={form} />
      <DocumentPaymentScheduleSection form={form} />
      <ContractWarrantySection />
      <ContractTermsSection />

      {/* register in the form */}
      <Form.Item<ContractFormData> name="visible_blocks" hidden>
        <input />
      </Form.Item>

      {/* Submit Button */}
      <Flex justify={"end"} className={"sticky z-10 bottom-0 bg-background"}>
        <Button
          loading={saving}
          disabled={hasErrors}
          type="primary"
          htmlType="submit"
        >
          Submit Contract
        </Button>
      </Flex>
    </Form>
  )
}
